import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  CircularProgress,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import axios from "axios";

dayjs.extend(customParseFormat);

const getAuthHeaders = () => ({
  "Content-Type": "application/json",
  "auth-key": (localStorage.getItem("token") || "").replace(/"/g, ""),
});

const formatDateTimeForAPI = (date = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${month}/${day}/${year} ${pad(hours)}:${minutes}:${seconds} ${ampm}`;
};

export default function ManualAttendanceDialog({
  open,
  onClose,
  rowData,
  onSuccess,
}) {
  const [submitCheckIn, setSubmitCheckIn] = useState(true);
  const [submitCheckOut, setSubmitCheckOut] = useState(false);
  const [date, setDate] = useState("");
  const [checkInTime, setCheckInTime] = useState("");
  const [checkOutTime, setCheckOutTime] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    if (rowData && open) {
      const formattedDate = rowData.Date
        ? dayjs(rowData.Date).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD");
      setDate(formattedDate);

      const parsedIn = rowData.InTime
        ? dayjs(rowData.InTime, ["hh:mm A", "HH:mm"])
        : null;
      const formattedIn =
        parsedIn && parsedIn.isValid() ? parsedIn.format("HH:mm") : "";
      setCheckInTime(formattedIn);

      const parsedOut = rowData.OutTime
        ? dayjs(rowData.OutTime, ["hh:mm A", "HH:mm"])
        : null;
      const formattedOut =
        parsedOut && parsedOut.isValid() ? parsedOut.format("HH:mm") : "";
      setCheckOutTime(formattedOut);

      // Default selection logic based on existing times
      if (!formattedIn) {
        setSubmitCheckIn(true);
        setSubmitCheckOut(false);
      } else if (formattedIn && !formattedOut) {
        setSubmitCheckIn(false);
        setSubmitCheckOut(true);
      } else {
        setSubmitCheckIn(true);
        setSubmitCheckOut(false);
      }

      setReason("");
      setMessage({ type: "", text: "" });
    }
  }, [rowData, open]);

  const fetchSiteLocations = async () => {
    const serviceNo = localStorage.getItem("ServiceNo");
    if (!serviceNo) {
      throw new Error("Service number not found. Please log in again.");
    }

    const response = await fetch(
      `${axios.defaults.baseURL}Attendance/GetDGESLocbySno?p_sno=${serviceNo}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error("Failed to fetch site location.");

    const data = await response.json();
    if (
      data.StatusCode !== 200 ||
      !data.ResultSet ||
      data.ResultSet.length === 0
    ) {
      throw new Error("No site location found for your service number.");
    }

    return data.ResultSet.map((record) => ({
      proNo: record.Pro_no,
      lat: parseFloat(record.Lat),
      lon: parseFloat(record.Lon),
      barNo: record.Bar_no,
      serviceNo: record.Service_no,
    }));
  };

  const getDeviceLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser."));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude, longitude } }) => {
          resolve({ latitude, longitude });
        },
        () =>
          reject(
            new Error(
              "Unable to access location. Please enable location services."
            )
          )
      );
    });

  const postAttendance = async ({
    barcodeNo,
    barcodeDate,
    pStatus,
    hstatus,
    latitude,
    longitude,
    reason = " ",
    clockNo,
  }) => {
    const body = {
      p_barcode_no: barcodeNo,
      p_barcode_date: barcodeDate,
      p_employee_type: "0",
      p_clock_no: clockNo,
      p_status: pStatus,
      p_hstatus: hstatus,
      p_latitude: String(latitude),
      p_longitude: String(longitude),
      p_reason: reason,
    };

    const response = await fetch(
      `${axios.defaults.baseURL}Attendance/DGESPostAttendance`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      }
    );
    if (!response.ok) {
      throw new Error("Failed to submit attendance. Please try again.");
    }

    const data = await response.json();
    if (data.StatusCode && data.StatusCode !== 200 && data.StatusCode !== 201) {
      throw new Error(data.Result || "Attendance submission failed.");
    }
    return data;
  };

  const sendManualAttendanceSms = async (message) => {
    const phoneNo = localStorage.getItem("PhoneNo")?.trim();
    if (!phoneNo) {
      throw new Error("Phone number not found.");
    }

    const smsUrl = new URL(
      "https://dgessmsgateway.dockyardsoftware.com/SMS/SendCmnsms"
    );
    smsUrl.searchParams.set("P_Contact", phoneNo);
    smsUrl.searchParams.set("P_Message", message);

    const response = await fetch(smsUrl.toString());
    if (!response.ok) {
      throw new Error(`SMS gateway request failed (HTTP ${response.status}).`);
    }
  };

  const handleSubmit = async () => {
    if (!date || !reason.trim()) {
      setMessage({
        type: "error",
        text: "Please fill in the date and reason.",
      });
      return;
    }
    if (!submitCheckIn && !submitCheckOut) {
      setMessage({
        type: "error",
        text: "Please select at least Check-In or Check-Out.",
      });
      return;
    }
    if (submitCheckIn && !checkInTime) {
      setMessage({ type: "error", text: "Please provide a Check-In time." });
      return;
    }
    if (submitCheckOut && !checkOutTime) {
      setMessage({ type: "error", text: "Please provide a Check-Out time." });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const sites = await fetchSiteLocations();
      const device = await getDeviceLocation();
      const [year, monthVal, dayVal] = date.split("-");
      const site = sites[0];

      if (submitCheckIn) {
        const [inHour, inMin] = checkInTime.split(":");
        const checkInDT = new Date(
          parseInt(year, 10),
          parseInt(monthVal, 10) - 1,
          parseInt(dayVal, 10),
          parseInt(inHour, 10),
          parseInt(inMin, 10),
          0
        );
        await postAttendance({
          barcodeNo: site.proNo,
          barcodeDate: formatDateTimeForAPI(checkInDT),
          pStatus: "I",
          hstatus: "P",
          latitude: device.latitude,
          longitude: device.longitude,
          reason,
          clockNo: 33,
        });
      }

      if (submitCheckOut) {
        const [outHour, outMin] = checkOutTime.split(":");
        const checkOutDT = new Date(
          parseInt(year, 10),
          parseInt(monthVal, 10) - 1,
          parseInt(dayVal, 10),
          parseInt(outHour, 10),
          parseInt(outMin, 10),
          0
        );
        await postAttendance({
          barcodeNo: site.proNo,
          barcodeDate: formatDateTimeForAPI(checkOutDT),
          pStatus: "O",
          hstatus: "P",
          latitude: device.latitude,
          longitude: device.longitude,
          reason,
          clockNo: 33,
        });
      }

      const submitted = [submitCheckIn && "Check-In", submitCheckOut && "Check-Out"]
        .filter(Boolean)
        .join(" & ");
      let smsWarning = "";
      try {
        await sendManualAttendanceSms(
          `Manual ${submitted} attendance for ${date} has been submitted for approval.`
        );
      } catch (smsError) {
        smsWarning = ` SMS notification could not be sent: ${smsError.message}`;
      }

      setMessage({
        type: smsWarning ? "warning" : "success",
        text: `Manual ${submitted} submitted for approval. Date: ${date}.${smsWarning || " SMS notification sent."}`,
      });

      if (onSuccess) {
        onSuccess();
      }

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#1976d2",
          color: "#fff",
          fontSize: "1rem",
          fontWeight: 600,
        }}
      >
        Manual Attendance Request
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          sx={{ color: "#fff" }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, pt: 3 }}>
        {message.text && (
          <Alert
            severity={message.type}
            sx={{ mb: 2 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Submit Request For:
        </Typography>

        <FormGroup row sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={submitCheckIn}
                onChange={(e) => setSubmitCheckIn(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="body2">Check-In</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={submitCheckOut}
                onChange={(e) => setSubmitCheckOut(e.target.checked)}
                color="warning"
                size="small"
              />
            }
            label={<Typography variant="body2">Check-Out</Typography>}
          />
        </FormGroup>

        <TextField
          fullWidth
          type="date"
          label="Date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ mb: 2 }}
        />

        {submitCheckIn && (
          <TextField
            fullWidth
            type="time"
            label="Check-In Time"
            value={checkInTime}
            onChange={(e) => setCheckInTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ mb: 2 }}
          />
        )}

        {submitCheckOut && (
          <TextField
            fullWidth
            type="time"
            label="Check-Out Time"
            value={checkOutTime}
            onChange={(e) => setCheckOutTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Reason for Request *"
          placeholder="e.g. Forgot to check in/out, Site visit, Technical issue..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          sx={{ mb: 1 }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          size="small"
          sx={{ color: "#666", borderColor: "#ccc" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          size="small"
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CheckCircleIcon />
            )
          }
          sx={{ backgroundColor: "#1976d2" }}
        >
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
